import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { CircleAlert, CloudUpload } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Button } from '@/components/ui';
import { formatCurrency, formatDateTime } from '@/utils/formatters';
import { useCheckoutQueue, restoreToCart, discardSale } from './checkoutQueue';

/** Pending / failed rwallet sales pinned above the transaction list (M8 notice rows). */
export const QueuedSalesBanner: React.FC<{ className?: string }> = ({ className }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queue = useCheckoutQueue();
    const cartItems = useAppSelector((s) => s.cart.items);

    if (queue.length === 0) return null;

    const restore = (id: string) => {
        if (cartItems.length > 0) {
            toast.error(t('queue.cartNotEmpty'));
            return;
        }
        restoreToCart(id, dispatch);
        navigate('/cart');
    };

    return (
        <div className={clsx('flex flex-col gap-2', className)}>
            {queue.map((sale) => (
                <div key={sale.id} className={clsx('flex items-start gap-2.5 rounded-md px-3.5 py-2.5 text-[13px] font-bold', sale.error ? 'bg-status-danger-soft text-danger-700' : 'bg-status-warning-soft text-accent-800')}>
                    {sale.error ? <CircleAlert className="mt-px h-[18px] w-[18px] shrink-0" /> : <CloudUpload className="mt-px h-[18px] w-[18px] shrink-0 animate-pulse" />}
                    <div className="min-w-0 flex-1">
                        <p>{sale.error ? t('queue.failed') : t('queue.pending')} · <span className="tnum">{formatCurrency(sale.request.payment.amount)}</span></p>
                        <p className="text-xs font-semibold opacity-80">
                            {formatDateTime(new Date(sale.queuedAt))} · {t('cart.itemsCount', { count: sale.cart.items.length })}{sale.cart.customerName && ` · ${sale.cart.customerName}`}
                        </p>
                        {sale.error && <p className="mt-1 text-xs font-semibold">{sale.error}</p>}
                    </div>
                    {sale.error && (
                        <div className="flex shrink-0 gap-1">
                            <Button size="sm" variant="outline" onClick={() => restore(sale.id)}>{t('queue.restore')}</Button>
                            <Button size="sm" variant="ghost" onClick={() => discardSale(sale.id)}>{t('queue.discard')}</Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default QueuedSalesBanner;
